import React, { useEffect, useState } from "react";
import { api, CARE_TYPES, FACILITY_TYPES, SPECIALTIES, CARE_NEEDS, FINANCIAL, AVAILABILITY, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ImagePlus, Trash2, Save, Mail, Phone, MapPin } from "lucide-react";
import MembershipCard from "@/components/MembershipCard";

const empty = {
  name: "",
  facility_type: "memory_care",
  address: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
  email: "",
  description: "",
  hours: "",
  price_range: "",
  care_types: [],
  specialties: [],
  care_needs: [],
  financial: [],
  availability: [],
  amenities: [],
  is_published: true,
};

export default function FacilityDashboard() {
  const [profile, setProfile] = useState(empty);
  const [photos, setPhotos] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [{ data: my }, { data: inq }, { data: pr }] = await Promise.all([
        api.get("/facilities/mine"),
        api.get("/inquiries/mine"),
        api.get("/billing/pricing"),
      ]);
      if (my.facility) {
        setProfile({ ...empty, ...my.facility });
        setPhotos(my.facility.photos || []);
      }
      setInquiries(inq.items || []);
      setPricing(pr);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const toggleCare = (val) => {
    setProfile((p) => ({
      ...p,
      care_types: p.care_types.includes(val)
        ? p.care_types.filter((c) => c !== val)
        : [...p.care_types, val],
    }));
  };

  const toggleSpecialty = (val) => {
    setProfile((p) => ({
      ...p,
      specialties: p.specialties.includes(val)
        ? p.specialties.filter((c) => c !== val)
        : [...p.specialties, val],
    }));
  };

  const toggleTag = (field, val) => {
    setProfile((p) => ({
      ...p,
      [field]: (p[field] || []).includes(val)
        ? p[field].filter((c) => c !== val)
        : [...(p[field] || []), val],
    }));
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = {
        name: profile.name,
        facility_type: profile.facility_type,
        address: profile.address,
        city: profile.city,
        state: profile.state,
        zip: profile.zip,
        phone: profile.phone,
        email: profile.email,
        description: profile.description,
        hours: profile.hours,
        price_range: profile.price_range,
        care_types: profile.care_types,
        specialties: profile.specialties,
        care_needs: profile.care_needs,
        financial: profile.financial,
        availability: profile.availability,
        amenities: typeof profile.amenities === "string"
          ? profile.amenities.split(",").map((s) => s.trim()).filter(Boolean)
          : profile.amenities,
        is_published: !!profile.is_published,
      };
      const { data } = await api.post("/facilities", payload);
      setProfile({ ...empty, ...data });
      setPhotos(data.photos || []);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photos.length >= 5) {
      toast.error("Maximum of 5 photos allowed");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/facilities/photos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotos((arr) => [...arr, data]);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = async (photoId) => {
    try {
      await api.delete(`/facilities/photos/${photoId}`);
      setPhotos((arr) => arr.filter((p) => p.id !== photoId));
      toast.success("Photo removed");
    } catch {
      toast.error("Could not remove photo");
    }
  };

  const updateInquiry = async (id, status) => {
    try {
      const { data } = await api.patch(`/inquiries/${id}`, { status });
      setInquiries((arr) => arr.map((i) => (i.id === id ? data : i)));
    } catch {
      toast.error("Could not update inquiry");
    }
  };

  if (loading) return <div className="px-6 py-20 text-center text-muted-foreground">Loading…</div>;

  return (
    <div data-testid="facility-dashboard" className="mx-auto max-w-6xl px-6 py-10 lg:px-8">
      <p className="text-sm uppercase tracking-[0.18em] text-primary">Member dashboard</p>
      <h1 className="mt-2 font-serif text-4xl text-forest sm:text-5xl">Your facility profile</h1>
      <p className="mt-2 text-muted-foreground">
        Only members of your home can edit this profile. Upload up to 5 photos and keep your drop-in
        and respite availability current.
      </p>

      <div className="mt-8 grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {profile.id && (
            <div className="mb-6">
              <MembershipCard facility={profile} price={pricing} />
            </div>
          )}
          <form onSubmit={save}>
            <Card className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
              <h2 className="font-serif text-2xl text-forest">Profile details</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-muted-foreground">Facility name</label>
                  <Input
                    required
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    data-testid="facility-name-input"
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Facility type</label>
                  <select
                    value={profile.facility_type}
                    onChange={(e) => setProfile({ ...profile, facility_type: e.target.value })}
                    data-testid="facility-type-select"
                    className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm"
                  >
                    {FACILITY_TYPES.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Price range</label>
                  <Input
                    value={profile.price_range}
                    onChange={(e) => setProfile({ ...profile, price_range: e.target.value })}
                    placeholder="e.g. $95–$160/day"
                    data-testid="facility-price-input"
                    className="mt-1 h-11"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-muted-foreground">Street address</label>
                  <Input
                    value={profile.address}
                    onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                    data-testid="facility-address-input"
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">City</label>
                  <Input
                    required
                    value={profile.city}
                    onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                    data-testid="facility-city-input"
                    className="mt-1 h-11"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">State</label>
                    <Input
                      required
                      maxLength={2}
                      value={profile.state}
                      onChange={(e) => setProfile({ ...profile, state: e.target.value })}
                      data-testid="facility-state-input"
                      className="mt-1 h-11"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">ZIP</label>
                    <Input
                      value={profile.zip}
                      onChange={(e) => setProfile({ ...profile, zip: e.target.value })}
                      data-testid="facility-zip-input"
                      className="mt-1 h-11"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Phone</label>
                  <Input
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    data-testid="facility-phone-input"
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Public email</label>
                  <Input
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    data-testid="facility-email-input"
                    className="mt-1 h-11"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-muted-foreground">Hours</label>
                  <Input
                    value={profile.hours}
                    onChange={(e) => setProfile({ ...profile, hours: e.target.value })}
                    placeholder="e.g. Mon–Fri 7:00am–6:30pm"
                    data-testid="facility-hours-input"
                    className="mt-1 h-11"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-muted-foreground">Description</label>
                  <Textarea
                    rows={4}
                    value={profile.description}
                    onChange={(e) => setProfile({ ...profile, description: e.target.value })}
                    data-testid="facility-description-input"
                    className="mt-1"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-muted-foreground">Amenities (comma-separated)</label>
                  <Input
                    value={Array.isArray(profile.amenities) ? profile.amenities.join(", ") : profile.amenities}
                    onChange={(e) => setProfile({ ...profile, amenities: e.target.value })}
                    placeholder="Sensory garden, RN on call, Hot lunch"
                    data-testid="facility-amenities-input"
                    className="mt-1 h-11"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-muted-foreground">Availability</label>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {CARE_TYPES.map((c) => (
                      <label key={c.value} className="flex items-center gap-2 rounded-xl border border-border/60 bg-background p-3 text-sm">
                        <Checkbox
                          checked={profile.care_types.includes(c.value)}
                          onCheckedChange={() => toggleCare(c.value)}
                          data-testid={`care-type-checkbox-${c.value}`}
                        />
                        {c.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-muted-foreground">Specialties</label>
                  <p className="mt-1 text-xs text-muted-foreground">Check what your home is trained or certified for. Caregivers can filter by these.</p>
                  <div className="mt-2 grid gap-2 sm:grid-cols-3">
                    {SPECIALTIES.map((s) => (
                      <label key={s.value} className="flex items-center gap-2 rounded-xl border border-emerald/30 bg-emerald/5 p-3 text-sm">
                        <Checkbox
                          checked={profile.specialties.includes(s.value)}
                          onCheckedChange={() => toggleSpecialty(s.value)}
                          data-testid={`specialty-checkbox-${s.value}`}
                        />
                        <span className="font-medium text-emerald">{s.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {[
                  { field: "care_needs", label: "Care needs we accept", options: CARE_NEEDS, color: "border-border/60 bg-background" },
                  { field: "financial", label: "Financial options accepted", options: FINANCIAL, color: "border-gold/30 bg-gold/5" },
                  { field: "availability", label: "Current availability", options: AVAILABILITY, color: "border-emerald/30 bg-emerald/5" },
                ].map((group) => (
                  <div key={group.field} className="sm:col-span-2">
                    <label className="text-xs font-medium text-muted-foreground">{group.label}</label>
                    <div className="mt-2 grid gap-2 sm:grid-cols-3">
                      {group.options.map((o) => (
                        <label key={o.value} className={`flex items-center gap-2 rounded-xl border ${group.color} p-3 text-sm`}>
                          <Checkbox
                            checked={(profile[group.field] || []).includes(o.value)}
                            onCheckedChange={() => toggleTag(group.field, o.value)}
                            data-testid={`${group.field}-checkbox-${o.value}`}
                          />
                          <span className="text-foreground">{o.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center justify-end gap-3">
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Checkbox
                    checked={profile.is_published}
                    onCheckedChange={(v) => setProfile({ ...profile, is_published: !!v })}
                    data-testid="facility-published-checkbox"
                  /> Listed publicly
                </label>
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="facility-save-btn"
                  className="rounded-full bg-primary px-6 text-primary-foreground"
                >
                  <Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save profile"}
                </Button>
              </div>
            </Card>
          </form>

          {/* Photos */}
          <Card className="mt-8 rounded-2xl border border-border/60 bg-card p-6 shadow-sm" data-testid="facility-photos-card">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-serif text-2xl text-forest">Photos</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Add 3–5 photos. {photos.length}/5 uploaded.
                </p>
              </div>
              <label className="inline-flex">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  onChange={upload}
                  disabled={uploading || photos.length >= 5}
                  data-testid="photo-upload-input"
                />
                <span className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-0.5">
                  <ImagePlus className="h-4 w-4" /> {uploading ? "Uploading…" : "Add photo"}
                </span>
              </label>
            </div>
            {photos.length === 0 ? (
              <p className="mt-6 rounded-xl border border-dashed border-border bg-muted/40 p-8 text-center text-sm text-muted-foreground">
                No photos yet. Caregivers value a warm, real view of your space.
              </p>
            ) : (
              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                {photos.map((p) => (
                  <div key={p.id} className="group relative overflow-hidden rounded-2xl border border-border/60" data-testid={`facility-photo-${p.id}`}>
                    <img src={fileUrl(p.path)} alt="" className="aspect-square w-full object-cover" />
                    <button
                      onClick={() => removePhoto(p.id)}
                      data-testid={`facility-photo-delete-${p.id}`}
                      className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-card/95 text-destructive shadow opacity-90 hover:opacity-100"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Inquiries */}
        <div className="lg:col-span-1">
          <Card className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm" data-testid="facility-inquiries-card">
            <h2 className="font-serif text-2xl text-forest">Recent inquiries</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Caregivers asking to drop in or try a respite stay.
            </p>
            <div className="mt-5 space-y-4">
              {inquiries.length === 0 && (
                <p className="rounded-xl border border-dashed border-border bg-muted/40 p-5 text-sm text-muted-foreground">
                  No inquiries yet. Save your profile and add photos to start receiving them.
                </p>
              )}
              {inquiries.map((i) => (
                <div key={i.id} data-testid={`inquiry-row-${i.id}`} className="rounded-xl border border-border/60 bg-background p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-foreground">{i.caregiver_name}</span>
                    <Badge variant="outline" className="rounded-full text-xs capitalize">{i.status}</Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {(CARE_TYPES.find((c) => c.value === i.care_type) || {}).label || i.care_type}
                    {i.requested_date ? ` · ${i.requested_date}` : ""}
                  </p>
                  {i.loved_one_name && (
                    <p className="mt-1 text-xs text-muted-foreground">For: {i.loved_one_name}</p>
                  )}
                  {i.message && (
                    <p className="mt-2 text-sm text-foreground">{i.message}</p>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <a href={`mailto:${i.caregiver_email}`} className="inline-flex items-center gap-1 hover:text-foreground"><Mail className="h-3 w-3" /> {i.caregiver_email}</a>
                    {i.caregiver_phone && (
                      <a href={`tel:${i.caregiver_phone}`} className="inline-flex items-center gap-1 hover:text-foreground"><Phone className="h-3 w-3" /> {i.caregiver_phone}</a>
                    )}
                  </div>
                  <div className="mt-3 flex gap-2">
                    {["responded", "scheduled", "closed"].map((s) => (
                      <Button
                        key={s}
                        size="sm"
                        variant={i.status === s ? "default" : "outline"}
                        onClick={() => updateInquiry(i.id, s)}
                        data-testid={`inquiry-${i.id}-set-${s}`}
                        className="rounded-full text-xs capitalize"
                      >
                        {s}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
